import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { MinusIcon, PlusIcon, RotateCcwIcon } from 'lucide-react';

import IconButton from '../ui/icon-button';
import { Button } from '../ui/shadcn/button';

import { loadArtwork, type Artwork } from './artwork';
import FloorMap from './floor-map';
import SpacePicker from './space-picker';
import {
  SPACES,
  findDesk,
  normaliseDeskId,
  spaceById,
  type SpaceDef,
  type SpaceId,
} from './spaces';
import { useMapZoomPan } from './use-map-zoom-pan';

import './seat-map.css';

/** Either one flat list of desk ids, or a list per space. */
export type BookedDesks = string[] | Partial<Record<SpaceId, string[]>>;

export interface SeatMapProps {
  /** Controlled space id. Omit to let the built-in picker own the selection. */
  space?: SpaceId;
  defaultSpace?: SpaceId;
  onSpaceChange?: (space: SpaceId) => void;

  /** Controlled desk selection. Omit to let the map own it. */
  selected?: string | null;
  onSelect?: (deskId: string | null, space: SpaceId) => void;

  /** Desk ids already taken. A flat array is split per space by each floor's desk list. */
  booked?: BookedDesks;

  /**
   * Called when the built-in Book button is pressed. If it returns a promise,
   * the map waits for it and only reports success when it resolves — a
   * rejected booking leaves the desk unconfirmed rather than lying.
   */
  onBook?: (deskId: string, space: SpaceId) => void | Promise<unknown>;

  /** Let a booked desk be picked, so an operator can inspect who holds it. */
  allowBookedSelection?: boolean;

  /** The map's own title/stats strip. Hide it when the page provides its own. */
  showHeader?: boolean;
  /** The built-in floor dropdown. Hide it when the page has its own picker. */
  showPicker?: boolean;
  /** The map's own status + Book button. Hide it to drive booking yourself. */
  showFooter?: boolean;
  /** The − / + buttons in the canvas corner. Clicking the floor zooms either way. */
  showZoom?: boolean;

  accent?: string;
  showPins?: boolean;
  /** The artwork's own Available / Booked / Selected key. */
  showLegend?: boolean;
  /** Crop the artwork's bottom margin to what is drawn, once the key is off. */
  trimFrame?: boolean;
  badgeScale?: number;
  /** A little character walks up to the selected desk, sits and opens a laptop. */
  showOccupant?: boolean;
  maxZoom?: number;
  initialRotation?: number;
  /**
   * Fill the height the host gives the map, e.g. a grid cell stretched to the
   * column beside it, instead of taking the artwork's own height. The artwork
   * scales to the largest size that fits, keeping its proportions. Where the
   * host gives no height (a stacked phone layout), `--map-min-height` sets it.
   */
  fill?: boolean;
  className?: string;
  /**
   * Lands on the map's own root, which is where the --map-* tokens have to be
   * set: the defaults in the stylesheet sit on .seatmap itself, so a value
   * inherited from a wrapper element would lose to them.
   */
  style?: CSSProperties;
}

function bookedFor(booked: BookedDesks | undefined, space: SpaceDef): Set<string> {
  if (!booked) return new Set();
  const list = Array.isArray(booked)
    ? booked.filter((id) => findDesk(space, id))
    : (booked[space.id] ?? []);
  return new Set(list.map(normaliseDeskId));
}

/**
 * Isometric seat map for the coworking floors, with a picker to switch between
 * them, viewBox-driven zoom/pan, and four rotation views.
 *
 * Only the active floor is mounted: both artworks carry the same fixed element
 * ids, so rendering both at once would duplicate ids in the document.
 */
export default function SeatMap({
  space,
  defaultSpace = SPACES[0].id,
  onSpaceChange,
  selected,
  onSelect,
  booked,
  onBook,
  allowBookedSelection = false,
  showHeader = true,
  showPicker = true,
  showFooter = true,
  showZoom = true,
  accent = '#2fbfc7',
  showPins = true,
  showLegend = true,
  trimFrame = false,
  badgeScale = 0.76,
  showOccupant = true,
  maxZoom = 6,
  initialRotation = 0,
  fill = false,
  className,
  style,
}: SeatMapProps) {
  const [ownSpace, setOwnSpace] = useState<SpaceId>(defaultSpace);
  const [ownSelected, setOwnSelected] = useState<string | null>(null);
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  const active = spaceById(space ?? ownSpace);
  const activeSelected = selected === undefined ? ownSelected : selected;
  const bookedSet = useMemo(() => bookedFor(booked, active), [booked, active]);

  // Load only the artwork for the floor being shown.
  useEffect(() => {
    let cancelled = false;
    setArtwork(null);
    loadArtwork(active.id).then((art) => {
      if (!cancelled) setArtwork(art);
    });
    return () => {
      cancelled = true;
    };
  }, [active.id]);

  const select = useCallback(
    (deskId: string | null) => {
      if (selected === undefined) setOwnSelected(deskId);
      setConfirmed(null);
      onSelect?.(deskId, active.id);
    },
    [selected, onSelect, active.id],
  );

  const changeSpace = useCallback(
    (next: SpaceId) => {
      if (space === undefined) setOwnSpace(next);
      // Desk ids are only unique within a space, so a stale pick must go.
      if (selected === undefined) setOwnSelected(null);
      setConfirmed(null);
      onSpaceChange?.(next);
      onSelect?.(null, next);
    },
    [space, selected, onSpaceChange, onSelect],
  );

  const book = useCallback(async () => {
    if (!activeSelected || !onBook) return;
    setBooking(true);
    try {
      // Await before confirming: an optimistic confirm would leave the map
      // claiming a desk is reserved even when the request failed.
      await onBook(activeSelected, active.id);
      setConfirmed(activeSelected);
    } catch {
      setConfirmed(null);
    } finally {
      setBooking(false);
    }
  }, [activeSelected, onBook, active.id]);

  const { zoomIn, zoomOut, reset, grabbing, canPan, atMin, atMax } = useMapZoomPan(hostRef, {
    resetKey: `${active.id}|${artwork ? 'ready' : 'loading'}`,
    max: maxZoom,
  });

  const selectedIsBooked = activeSelected ? bookedSet.has(activeSelected) : false;
  const status = confirmed
    ? `Desk ${confirmed} reserved for today, 9:00 AM – 6:00 PM.`
    : activeSelected
      ? `Desk ${activeSelected} selected for the full day, 9:00 AM – 6:00 PM.`
      : `Tap a desk to select it. ${bookedSet.size} of ${active.seats} already booked today.`;

  return (
    <div
      ref={hostRef}
      className={
        'seatmap' +
        (!showHeader && !showPicker ? ' seatmap--bare' : '') +
        (fill ? ' seatmap--fill' : '') +
        (canPan ? ' seatmap--zoomed' : '') +
        (grabbing ? ' seatmap--grabbing' : '') +
        (className ? ' ' + className : '')
      }
      style={{ ...style, '--accent': accent } as CSSProperties}
    >
      {showHeader && (
        <div className="seatmap__row seatmap__head">
          <div className="seatmap__headings">
            <div className="seatmap__eyebrow">
              {active.name.toUpperCase()} · {active.zone.toUpperCase()}
            </div>
            <div className="seatmap__title">Pick your desk</div>
          </div>
          <div className="seatmap__stats">
            <div className="seatmap__stat">
              <span>SEATS</span>
              <span>{active.seats} bench</span>
            </div>
            <div className="seatmap__stat">
              <span>ROOM</span>
              <span>{active.room}</span>
            </div>
            <div className="seatmap__stat">
              <span>{active.groupsLabel}</span>
              <span>{active.groups}</span>
            </div>
          </div>
        </div>
      )}

      {showPicker && (
        <div className="seatmap__row seatmap__bar">
          <SpacePicker value={active.id} onChange={changeSpace} />
        </div>
      )}

      {artwork ? (
        <FloorMap
          key={active.id}
          space={active}
          artwork={artwork}
          booked={bookedSet}
          selected={activeSelected}
          onSelect={select}
          allowBookedSelection={allowBookedSelection}
          showPins={active.hasPins && showPins}
          showLegend={showLegend}
          trimFrame={trimFrame}
          badgeScale={badgeScale}
          showOccupant={showOccupant}
          initialRotation={initialRotation}
          controls={
            (canPan || showZoom) && (
              <>
                {/* Only once there is something to undo: panning needs a zoom first. */}
                {canPan && (
                  <Button variant="outline" size="sm" onClick={reset}>
                    <RotateCcwIcon aria-hidden="true" />
                    Reset
                  </Button>
                )}
                {showZoom && (
                  <div className="seatmap__zoomstack">
                    <IconButton label="Zoom in" size="compact" onClick={zoomIn} disabled={atMax}>
                      <PlusIcon aria-hidden="true" className="size-4" />
                    </IconButton>
                    <IconButton label="Zoom out" size="compact" onClick={zoomOut} disabled={atMin}>
                      <MinusIcon aria-hidden="true" className="size-4" />
                    </IconButton>
                  </div>
                )}
              </>
            )
          }
        />
      ) : (
        <div className="seatmap__canvas seatmap__canvas--loading">
          <span className="seatmap__loading">Loading {active.name.toLowerCase()}…</span>
        </div>
      )}

      {showFooter && (
        <div className="seatmap__row seatmap__foot">
          <div className="seatmap__status">{status}</div>
          <div className="seatmap__actions">
            <Button variant="outline" onClick={() => select(null)}>
              Clear
            </Button>
            <Button
              // Disabled once booked too, so the same desk cannot be sent twice.
              disabled={!activeSelected || selectedIsBooked || booking || !!confirmed}
              onClick={book}
            >
              {booking ? 'Booking…' : activeSelected ? `Book ${activeSelected}` : 'Book desk'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
